import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { captureUtm } from "@/lib/utm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import ChatWidget from "@/components/ChatWidget";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, MessagesSquare, Mail, Users, Clock, TrendingUp, Shield, Sparkles, Check } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

function DemoDialog({ triggerLabel = "Book a demo", triggerClass = "", testId = "demo-open-btn" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", business_name: "", website: "", monthly_leads: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/demo-request", form);
      toast.success("Booked — we'll email you within 1 business day.");
      setOpen(false);
      setForm({ name: "", email: "", business_name: "", website: "", monthly_leads: "", notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={triggerClass} data-testid={testId}>{triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#f9f8f6] border-[#e5e2da] max-w-lg" data-testid="demo-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl leading-tight">Book a 20-min demo</DialogTitle>
          <DialogDescription className="text-[#5c5c5c]">
            We'll show you exactly how Coachly qualifies your traffic and books more calls.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Name</Label>
              <Input required data-testid="demo-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Email</Label>
              <Input required type="email" data-testid="demo-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Business</Label>
            <Input data-testid="demo-business-input" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Website</Label>
            <Input data-testid="demo-website-input" placeholder="https://" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Monthly inbound leads</Label>
            <Select value={form.monthly_leads} onValueChange={(v) => setForm({ ...form, monthly_leads: v })}>
              <SelectTrigger data-testid="demo-leads-select" className="mt-1 bg-white border-[#e5e2da] focus:ring-[#163325]">
                <SelectValue placeholder="Choose a range" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="0-25">0 – 25 / month</SelectItem>
                <SelectItem value="25-100">25 – 100 / month</SelectItem>
                <SelectItem value="100-500">100 – 500 / month</SelectItem>
                <SelectItem value="500+">500+ / month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Notes</Label>
            <Textarea data-testid="demo-notes-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="demo-submit-btn" className="w-full rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] h-11">
            {submitting ? "Sending…" : "Book my demo"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Landing() {
  useEffect(() => { captureUtm(); }, []);
  return (
    <div className="min-h-screen" style={{ background: "#f9f8f6" }}>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#e5e2da] bg-white px-3 py-1 text-xs uppercase tracking-widest text-[#5c5c5c]" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-[#d4a373]" /> Built for coaches
            </div>
            <h1 className="font-serif-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1c1c1c]" data-testid="hero-headline">
              Stop losing warm leads because you <span className="deco-underline">replied too late</span>.
            </h1>
            <p className="mt-6 text-lg text-[#3c3c3c] max-w-xl leading-relaxed" data-testid="hero-subtitle">
              Coachly is an AI sales assistant that qualifies your website visitors 24/7 and follows up by email until they book a call. Built for solo coaches and small coaching teams with inbound traffic but weak follow-up.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/plans" data-testid="hero-cta-signup">
                <Button className="rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] h-12 px-6 text-base" style={{ transition: "transform 160ms" }}>
                  Start 7-day trial <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <DemoDialog triggerLabel="Book a demo" triggerClass="rounded-full h-12 px-6 text-base border border-[#163325] bg-transparent text-[#163325] hover:bg-[#ebe8e0]" testId="hero-cta-demo" />
            </div>
            <div className="mt-8 flex items-center gap-5 text-xs text-[#5c5c5c]">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#163325]" /> Card required</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#163325]" /> Cancel anytime</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#163325]" /> No setup fees</div>
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.2 }} className="lg:col-span-5 relative">
            <div className="rounded-2xl border border-[#e5e2da] bg-white p-4" style={{ boxShadow: "0 20px 60px rgba(22,51,37,0.10)" }} data-testid="hero-chat-preview">
              <div className="flex items-center gap-2 pb-3 border-b border-[#e5e2da]">
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-serif-display" style={{ background: "#163325", color: "#f9f8f6" }}>C</div>
                <div>
                  <div className="text-sm font-medium">Coachly Assistant</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5c5c5c]">Live · replies in 2s</div>
                </div>
              </div>
              <div className="py-4 space-y-3 text-sm">
                <div className="max-w-[85%] px-3 py-2 rounded-2xl" style={{ background: "#ebe8e0", borderBottomLeftRadius: 4 }}>
                  Hey! What brought you to the site today?
                </div>
                <div className="max-w-[85%] ml-auto px-3 py-2 rounded-2xl text-[#f9f8f6]" style={{ background: "#163325", borderBottomRightRadius: 4 }}>
                  I run a mindset coaching business, want more discovery calls.
                </div>
                <div className="max-w-[85%] px-3 py-2 rounded-2xl" style={{ background: "#ebe8e0", borderBottomLeftRadius: 4 }}>
                  Love it. Roughly how many leads reach out per month right now?
                </div>
                <div className="max-w-[85%] ml-auto px-3 py-2 rounded-2xl text-[#f9f8f6]" style={{ background: "#163325", borderBottomRightRadius: 4 }}>
                  ~40, but only 3 book.
                </div>
                <div className="max-w-[90%] px-3 py-2 rounded-2xl" style={{ background: "#ebe8e0", borderBottomLeftRadius: 4 }}>
                  Common — usually a follow-up gap. What's your name so I can loop you in?
                </div>
              </div>
              <div className="border-t border-[#e5e2da] pt-3 flex items-center gap-2">
                <div className="flex-1 rounded-full bg-[#f9f8f6] border border-[#e5e2da] px-3 py-1.5 text-xs text-[#8b8b8b]">Type a message…</div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#163325" }}>
                  <ArrowRight className="w-3.5 h-3.5 text-[#f9f8f6]" />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 rounded-2xl border border-[#e5e2da] bg-white p-4 flex items-center gap-3 hidden lg:flex" data-testid="hero-badge-card">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#d4a373" }}>
                <Mail className="w-5 h-5 text-[#163325]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#5c5c5c]">Next up</div>
                <div className="text-sm font-medium">Follow-up email #1 scheduled</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-[#e5e2da]" style={{ background: "#efece5" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div><div className="font-serif-display text-3xl text-[#163325]">3.4×</div><div className="text-xs uppercase tracking-widest text-[#5c5c5c] mt-1">more booked calls</div></div>
          <div><div className="font-serif-display text-3xl text-[#163325]">&lt; 30s</div><div className="text-xs uppercase tracking-widest text-[#5c5c5c] mt-1">lead response time</div></div>
          <div><div className="font-serif-display text-3xl text-[#163325]">42%</div><div className="text-xs uppercase tracking-widest text-[#5c5c5c] mt-1">reply rate on emails</div></div>
          <div><div className="font-serif-display text-3xl text-[#163325]">24/7</div><div className="text-xs uppercase tracking-widest text-[#5c5c5c] mt-1">on your site</div></div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">The problem</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Your best leads leave before you reply.</h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Clock, title: "Slow follow-up kills conversion", body: "Leads that hear back in 5 minutes are 9× more likely to book than those hearing back in an hour." },
              { icon: Users, title: "Manual chasing doesn't scale", body: "You're a coach, not a call-centre. Every extra lead costs you time you don't have." },
              { icon: Mail, title: "Nurture emails go stale", body: "Generic drip sequences don't reference the visitor's actual problem, so they get archived." },
              { icon: MessagesSquare, title: "You lose context", body: "Website chat, form fills, and emails live in separate tabs. Nothing tells you if a lead is warm." },
            ].map((f, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-[#e5e2da] bg-white p-6"
                data-testid={`problem-card-${i}`}
              >
                <f.icon className="w-6 h-6 text-[#163325]" strokeWidth={1.5} />
                <div className="mt-4 font-medium">{f.title}</div>
                <p className="mt-2 text-sm text-[#5c5c5c] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 lg:py-28 border-t border-[#e5e2da]" style={{ background: "#efece5" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">How it works</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Capture → Qualify → Follow up → Convert.</h2>
            <p className="mt-4 text-[#3c3c3c]">One system that carries a lead from their first visit to your calendar.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-4 gap-4">
            {[
              { n: "01", t: "Capture", d: "Coachly greets every visitor and starts a real conversation — not a form.", icon: MessagesSquare },
              { n: "02", t: "Qualify", d: "It asks the right discovery questions and tags leads by fit and urgency.", icon: Sparkles },
              { n: "03", t: "Follow up", d: "A 4-step email sequence keeps replying until they book or opt out.", icon: Mail },
              { n: "04", t: "Convert", d: "You wake up to warm leads on your calendar with full transcript context.", icon: TrendingUp },
            ].map((s, i) => (
              <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: i * 0.1 }} className="rounded-2xl bg-white border border-[#e5e2da] p-6" data-testid={`how-step-${i}`}>
                <div className="text-xs uppercase tracking-[0.2em] text-[#d4a373]">Step {s.n}</div>
                <s.icon className="w-6 h-6 text-[#163325] mt-4" strokeWidth={1.5} />
                <div className="font-serif-display text-2xl mt-3">{s.t}</div>
                <p className="mt-2 text-sm text-[#5c5c5c]">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature bento */}
      <section className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Inside Coachly</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Everything to move a lead — nothing you don't need.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 md:row-span-2 rounded-2xl border border-[#e5e2da] bg-white p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-[#d4a373]">Live conversation</div>
              <h3 className="font-serif-display text-3xl mt-2">AI that sounds like a coach, not a chatbot.</h3>
              <p className="mt-3 text-[#5c5c5c] max-w-lg">Trained to ask discovery questions, capture context, and hand off warm leads. Powered by Claude Sonnet.</p>
              <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGNvYWNoJTIwc21pbGluZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NTcxMzQ1OHww&ixlib=rb-4.1.0&q=85" alt="Coach" className="mt-6 rounded-xl h-72 w-full object-cover" />
            </div>
            <div className="rounded-2xl border border-[#e5e2da] bg-[#163325] text-[#f9f8f6] p-8">
              <Mail className="w-6 h-6 text-[#d4a373]" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl mt-4">4-step nurture, built-in</h3>
              <p className="mt-3 text-sm opacity-80">Every captured lead gets a personalised email sequence starting instantly.</p>
            </div>
            <div className="rounded-2xl border border-[#e5e2da] bg-white p-8">
              <TrendingUp className="w-6 h-6 text-[#163325]" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl mt-4">Conversion dashboard</h3>
              <p className="mt-3 text-sm text-[#5c5c5c]">See booked calls, response rates and revenue at a glance.</p>
            </div>
            <div className="rounded-2xl border border-[#e5e2da] bg-white p-8">
              <Shield className="w-6 h-6 text-[#163325]" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl mt-4">Your voice, your rules</h3>
              <p className="mt-3 text-sm text-[#5c5c5c]">Bring your qualification questions. Coachly follows your script — not a template.</p>
            </div>
            <div className="rounded-2xl border border-[#e5e2da] bg-white p-8">
              <Sparkles className="w-6 h-6 text-[#d4a373]" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl mt-4">One-click setup</h3>
              <p className="mt-3 text-sm text-[#5c5c5c]">Paste a single script tag. Coachly is live on your site in under 5 minutes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section id="proof" className="py-24 lg:py-28 border-t border-[#e5e2da]" style={{ background: "#efece5" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Proof</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Coaches booking more calls, without hiring.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { quote: "I stopped losing weekend leads. Coachly captured 7 while I was on vacation and 3 turned into paid clients.", name: "Renée K.", role: "Business coach · Austin", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGNvYWNoJTIwc21pbGluZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NTcxMzQ1OHww&ixlib=rb-4.1.0&q=85" },
              { quote: "The follow-up sequence is what actually moves people. My reply rate went from 8% to 34% in two weeks.", name: "Marcus O.", role: "Executive coach · London", img: "https://images.unsplash.com/photo-1676989880361-091e12efc056?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGNvYWNoJTIwc21pbGluZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NTcxMzQ1OHww&ixlib=rb-4.1.0&q=85" },
              { quote: "Feels like a human on my site. My leads say the conversation is actually helpful before they book.", name: "Priya S.", role: "Career coach · Toronto", img: null },
            ].map((t, i) => (
              <div key={i} className="rounded-2xl bg-white border border-[#e5e2da] p-6" data-testid={`testimonial-${i}`}>
                <p className="font-serif-display text-xl leading-snug">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  {t.img ? (
                    <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-[#f9f8f6] font-serif-display" style={{ background: "#8c9b86" }}>{t.name[0]}</div>
                  )}
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-[#5c5c5c]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Pricing</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Simple pricing. Grow when you're ready.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { name: "Starter", price: "$49", tag: "For solo coaches", features: ["500 chats/mo", "Unlimited leads", "4-step email sequence", "Basic analytics", "7-day free trial"], cta: "Start 7-day trial", primary: false, planId: "starter" },
              { name: "Pro", price: "$149", tag: "Most popular", features: ["Unlimited chats", "3 email sequences", "Custom qualification script", "Advanced analytics", "CRM export", "Billed today · instant access"], cta: "Subscribe now", primary: true, planId: "pro" },
              { name: "Premium Setup", price: "$1,290", tag: "Done-for-you", features: ["Everything in Pro", "Custom script + brand voice", "Sequence copywriting", "White-glove onboarding"], cta: "Book a call", primary: false, planId: null },
            ].map((p, i) => (
              <div
                key={i}
                className={`rounded-2xl p-8 border ${p.primary ? "border-[#163325] bg-[#163325] text-[#f9f8f6]" : "border-[#e5e2da] bg-white"}`}
                data-testid={`pricing-${p.name.toLowerCase().replace(" ", "-")}`}
              >
                <div className={`text-xs uppercase tracking-[0.2em] ${p.primary ? "text-[#d4a373]" : "text-[#8c9b86]"}`}>{p.tag}</div>
                <div className="font-serif-display text-3xl mt-2">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-serif-display text-5xl">{p.price}</span>
                  {p.name !== "Premium Setup" && <span className={p.primary ? "opacity-80" : "text-[#5c5c5c]"}>/ month</span>}
                </div>
                <ul className={`mt-6 space-y-2 text-sm ${p.primary ? "opacity-90" : "text-[#3c3c3c]"}`}>
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2"><Check className={`w-4 h-4 mt-0.5 ${p.primary ? "text-[#d4a373]" : "text-[#163325]"}`} /> {f}</li>
                  ))}
                </ul>
                <div className="mt-8">
                  {p.name === "Premium Setup" ? (
                    <DemoDialog triggerLabel={p.cta} triggerClass="w-full rounded-full h-11 bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6]" testId={`pricing-cta-${i}`} />
                  ) : (
                    <Link to={`/signup?plan=${p.planId}`} data-testid={`pricing-cta-${i}`}>
                      <Button className={`w-full rounded-full h-11 ${p.primary ? "bg-[#d4a373] hover:bg-[#c99569] text-[#163325]" : "bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6]"}`}>{p.cta}</Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 lg:py-28 border-t border-[#e5e2da]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Common questions</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Everything you were going to ask.</h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {[
              { q: "How is this different from a normal chatbot?", a: "Most chatbots read a script and drop off. Coachly holds a real discovery conversation, tags the lead, then keeps replying by email for four days until they book — one system, not three." },
              { q: "What if a lead doesn't reply on chat?", a: "As soon as Coachly captures a name and email, it kicks off a personalised 4-email nurture sequence. You see every send in your dashboard." },
              { q: "Do I need to hire a copywriter?", a: "No. The default sequence is written for coaches and works out of the box. Pro and Premium let you edit or add your own." },
              { q: "Can I use my own domain and voice?", a: "Yes. Pro lets you upload your qualification questions and adjust the tone. Premium includes done-for-you copywriting." },
              { q: "Is there a free trial?", a: "Yes — the Starter plan includes a 7-day free trial. Card is required at signup but you're not charged until day 7. Pro is billed immediately for instant full access. Cancel either at any time from the billing portal." },
            ].map((f, i) => (
              <AccordionItem value={`item-${i}`} key={i} className="border-b border-[#e5e2da]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="font-serif-display text-xl text-left hover:no-underline py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#5c5c5c] leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl px-8 md:px-14 py-16 md:py-20 text-[#f9f8f6] relative overflow-hidden" style={{ background: "#163325" }}>
            <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#d4a373]">Ready?</div>
                <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">Turn today's traffic into next week's calls.</h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
                <Link to="/plans" data-testid="final-cta-signup"><Button className="rounded-full bg-[#d4a373] hover:bg-[#c99569] text-[#163325] h-12 px-6 text-base w-full">Start 7-day trial</Button></Link>
                <DemoDialog triggerLabel="Book a demo" triggerClass="rounded-full h-12 px-6 text-base border border-[#f9f8f6]/40 bg-transparent text-[#f9f8f6] hover:bg-white/10 w-full" testId="final-cta-demo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
}
